import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import map from 'lodash/map'
import size from 'lodash/size'
import reject from 'lodash/reject'
import noop from 'lodash/noop'
import get from 'lodash/get'

import {useCollectionData} from 'react-firebase-hooks/firestore'
import {db} from 'configs/firebase'

import Message from './Message'
import TypingIndicator from './TypingIndicator'
import EncryptionNote from './EncryptionNote'

import styles from './styles.module.scss'

const Messages = ({
  userId,
  roomId,
  messages,
  keyManager,
  encryptionKeys,
  height,
  hasMore,
  isLoading,
  isLoadingMore,
  onClickLoadMore,
}) => {
  const typingUsersDbRef = db
    .collection(`chats/${roomId}/typings`)
    .where('isTyping', '==', true)
  const [typingUsers] = useCollectionData(roomId ? typingUsersDbRef : null)

  const typingUsersNames = useMemo(
    () => map(reject(typingUsers, {uid: userId}), 'user.name'),
    [typingUsers, userId]
  )

  const typingIndicatorNode = size(typingUsersNames) ? (
    <TypingIndicator names={typingUsersNames} />
  ) : null

  const messagesNode = useMemo(() => {
    if (!size(messages)) return null

    return map(messages, m => (
      <div key={m.uid} className="column is-full">
        <Message
          keyManager={keyManager}
          createdAt={m.createdAt}
          from={get(m.from, 'name')}
          message={m.message}
          isFromSelf={get(m.from, 'uid') === userId}
        />
      </div>
    ))
  }, [userId, messages, keyManager])

  return (
    <div style={{height, maxHeight: height}} className={styles.container}>
      {!hasMore && (
        <div className="columns is-centered is-gapless mb-0">
          <div className="column is-full-mobile is-two-thirds-tablet is-three-fifths-desktop is-half-widescreen">
            <EncryptionNote encryptionKeys={encryptionKeys} />
          </div>
        </div>
      )}

      {(isLoading || isLoadingMore) && (
        <div className={classNames('mb-2', styles.loader)}>Loading...</div>
      )}
      {hasMore && !isLoading && !isLoadingMore && (
        <button
          className={classNames(
            'mb-2 is-block button is-white is-small is-rounded',
            styles.loadMore
          )}
          onClick={onClickLoadMore}>
          Load Previous
        </button>
      )}

      <div className="columns is-multiline is-mobile is-gapless reverse-row-order mb-1">
        {messagesNode}
      </div>

      {typingIndicatorNode && (
        <div className="columns is-multiline is-mobile is-gapless mb-1">
          <div className="column is-full">{typingIndicatorNode}</div>
        </div>
      )}
    </div>
  )
}

Messages.defaultProps = {
  messages: [],
  height: 320,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  onClickLoadMore: noop,
}

Messages.propTypes = {
  encryptionKeys: PropTypes.shape({
    public: PropTypes.string,
    private: PropTypes.string,
  }),
  hasMore: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  keyManager: PropTypes.object.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string.isRequired,
      createdAt: PropTypes.number,
      from: PropTypes.shape({
        uid: PropTypes.string,
        name: PropTypes.string,
      }),
      isFromSelf: PropTypes.bool,
      message: PropTypes.string,
    })
  ),
  onClickLoadMore: PropTypes.func,
  roomId: PropTypes.string,
  userId: PropTypes.string,
}

export default Messages
